import { shallowEqual } from 'react-redux';

import { NsCard } from 'components/common/ns-card/NsCard';
import { NsContent } from 'components/common/ns-content/NsContent';
import { NsDescriptionList } from 'components/common/ns-description-list/NsDescriptionList';
import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';

import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectSlice as selectConsumption } from 'state/consumption/consumption-slice';

import { useAppSelector } from 'hooks/useAppSelector';
import { useNavigation } from 'hooks/useNavigation';
import { useConsumptionEntry } from 'components/direct-sales/energy-needs/hooks/useConsumptionEntry';

import { capitalize } from 'utils/misc';
import { FUEL_OPTIONS } from 'config/app-config';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { formatEnergy } from 'utils/numberFormat';

const formatEnergyOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
};
type FuelConsumptionProps = {
  fuelType?: boolean;
  fuelName?: 'Electricity' | 'Gas';
  consumption?: number;
};

const FuelConsumption = ({
  fuelType,
  fuelName,
  consumption
}: FuelConsumptionProps) => {
  if (!fuelType) {
    return null;
  }

  return (
    <>
      <strong>{fuelName}: </strong>
      {formatEnergy(consumption, 'kwh', formatEnergyOptions)}{' '}
    </>
  );
};

export const PersonalInformationSummaryEnergyNeeds = () => {
  const fuelType = useAppSelector(selectFuelType, shallowEqual);
  const consumption = useAppSelector(selectConsumption, shallowEqual);
  const { isActual } = useConsumptionEntry();

  const { eventTypes, next } = useNavigation();

  const fuelValue = getFuelValue(fuelType);
  const fuelTypeText =
    fuelValue === FUEL_OPTIONS.DUAL
      ? 'Gas & electricity'
      : capitalize(fuelValue);

  const changeEnergyNeeds = () => {
    next({ eventType: eventTypes.CHANGE_ENERGY_NEEDS, wasVisited: true });
  };
  return (
    <>
      <NsContent spacing="normal">
        <h2 className="h4">Your energy needs</h2>
      </NsContent>

      <NsCard>
        <NsDescriptionList>
          {[
            { term: 'Fuel type', description: fuelTypeText },
            {
              term: isActual ? 'Actual usage' : 'Estimated usage',
              description: [
                <>
                  <FuelConsumption
                    fuelType={fuelType.electric}
                    fuelName="Electricity"
                    consumption={consumption.electric}
                  />
                </>,
                <>
                  <FuelConsumption
                    fuelType={fuelType.gas}
                    fuelName="Gas"
                    consumption={consumption.gas}
                  />
                </>,
                <>
                  <NsSecondaryCta onClick={changeEnergyNeeds} icon="">
                    Edit energy needs
                  </NsSecondaryCta>
                </>
              ]
            }
          ]}
        </NsDescriptionList>
      </NsCard>
    </>
  );
};

export default PersonalInformationSummaryEnergyNeeds;
